import { useMemo, useState, useEffect } from 'react';
import { UserType, MenuPermission, User } from '@/types';
import { apiService } from '@/services/api';

/**
 * Хук для перевірки прав доступу користувача
 */
export const usePermissions = (user: User | null) => {
  const [allowedResources, setAllowedResources] = useState<Set<string> | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Завантажуємо permissions для адмінів
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user || user.userType !== 'admin' || !user.roleId) {
        setAllowedResources(null);
        return;
      }

      setLoadingPermissions(true);
      try {
        const role = await apiService.getRole(user.roleId);
        const resources = new Set(role.permissions.map(p => p.resource.name));
        setAllowedResources(resources);
      } catch (error) {
        console.error('Error loading role permissions:', error);
        setAllowedResources(null);
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadPermissions();
  }, [user?.roleId, user?.userType]);

  /**
   * Перевіряє, чи має користувач доступ до певного елементу меню
   */
  const hasPermission = useMemo(() => {
    return (permission?: MenuPermission): boolean => {
      // Якщо немає обмежень доступу, доступ дозволений
      if (!permission) {
        return true;
      }

      // Якщо користувач не авторизований, доступ заборонений
      if (!user || !user.userType) {
        return false;
      }

      const userType = user.userType;

      // Спочатку перевіряємо userTypes
      const hasUserTypeAccess = permission.requireAll
        ? permission.userTypes.every(type => type === userType)
        : permission.userTypes.includes(userType);

      if (!hasUserTypeAccess) {
        return false;
      }

      // Для адмінів перевіряємо ACL через resource name, якщо він вказаний
      if (userType === 'admin' && permission.resourceName) {
        // Якщо permissions ще завантажуються, не показуємо пункт меню
        if (loadingPermissions) {
          return false;
        }
        // Якщо allowedResources null (помилка завантаження), не показуємо
        if (allowedResources === null) {
          return false;
        }
        // Перевіряємо, чи є ресурс в дозволених
        return allowedResources.has(permission.resourceName);
      }

      // Якщо адмін, але resourceName не вказано, або клієнт - дозволяємо доступ
      return true;
    };
  }, [user, allowedResources, loadingPermissions]);

  /**
   * Перевіряє, чи має користувач певний тип
   */
  const hasUserType = useMemo(() => {
    return (type: UserType): boolean => {
      return user?.userType === type;
    };
  }, [user]);

  /**
   * Перевіряє, чи є користувач адміністратором
   */
  const isAdmin = useMemo(() => {
    return hasUserType('admin');
  }, [hasUserType]);

  /**
   * Перевіряє, чи є користувач клієнтом
   */
  const isCustomer = useMemo(() => {
    return hasUserType('customer');
  }, [hasUserType]);

  return {
    hasPermission,
    hasUserType,
    isAdmin,
    isCustomer,
  };
};
